import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "@/constants/roles";
import { AdminApp } from "@/components/admin/AdminApp";
import { ManagerApp } from "@/components/manager/ManagerApp";
import { PermsProvider } from "@/components/PermsContext";
import { ApplicantDashboard } from "@/components/dashboard/ApplicantDashboard";
import { WorkerDashboard } from "@/components/dashboard/WorkerDashboard";
import { DeactivatedView } from "@/components/dashboard/DeactivatedView";
import { ViewAsBanner } from "@/components/dashboard/ViewAsBanner";
import { applyTheme } from "@/components/dashboard/ThemeToggle";

export default function Dashboard() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("melto_theme") || "dark");
  const navigate = useNavigate();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const yt = p.get("yt");
    if (!yt) return;
    if (yt === "connected") toast.success("YouTube connected — open Videos and press Sync now to pull your channel stats.");
    else if (yt === "no-refresh") toast.error("Google did not grant offline access (no refresh token) — press Connect again and approve all permissions.");
    else if (yt === "exchange-failed") toast.error("Google rejected the authorization code — please press Connect again. If it repeats, check the OAuth client configuration.");
    else toast.error("YouTube connection failed — try again from Videos.");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("melto_token");
    if (!token) {
      navigate("/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setMe(r.data);
        if (r.data.theme && r.data.theme !== theme) setTheme(r.data.theme);
      })
      .catch(() => {
        localStorage.removeItem("melto_token");
        navigate("/login");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("melto_theme", next);
    axios
      .patch(`${API}/my/theme`, { theme: next }, { headers: { Authorization: `Bearer ${localStorage.getItem("melto_token")}` } })
      .catch(() => {});
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex min-h-screen items-center justify-center gap-3 bg-[var(--pv-bg)] text-sm font-medium text-[var(--pv-text-3)]">
        <Loader2 className="h-5 w-5 animate-spin text-cyan-500" /> Loading your dashboard...
      </div>
    );
  }
  if (!me) return null;
  const themeProps = { theme, onToggleTheme: toggleTheme };
  let view;
  if (me.role === "admin") view = <PermsProvider><AdminApp me={me} {...themeProps} /></PermsProvider>;
  else if (me.role === "manager") view = <PermsProvider><ManagerApp me={me} {...themeProps} /></PermsProvider>;
  else if (me.deactivated) view = <DeactivatedView me={me} {...themeProps} />;
  else if (me.role === "worker") view = <WorkerDashboard me={me} {...themeProps} />;
  else view = <ApplicantDashboard me={me} {...themeProps} />;
  return (
    <>
      {me?.demo && (
        <div data-testid="demo-mode-banner" className="sticky top-0 z-[70] flex items-center justify-center gap-2 bg-amber-400 px-3 py-1.5">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-900" />
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-950">Demo mode — fully synthetic data · nothing here is real or saved to the live workspace</p>
        </div>
      )}
      <ViewAsBanner />
      {view}
    </>
  );
}
